import { clamp, hash01, mixHexColors } from "./chunk-0.jsx";
import { ChamiiCutePupil } from "./chunk-10.jsx";
export function ChamiiCuteEye({
  side,
  profile,
  blinkToken,
  active = true,
  motionBoost = false,
  seedKey
}) {
  const isLeft = side === 'left';
  const wink = profile.winkLeft && isLeft;
  const close = clamp(1 - (profile.openness ?? 0.78), 0, 0.95);
  const winkClose = wink ? 0.95 : close;
  const tilt = (profile.tilt || 0) * (isLeft ? -1 : 1);
  const outlineColor = profile.sticker ? 'rgba(15,23,42,0.65)' : 'rgba(255,255,255,0.25)';
  const seed = hash01(seedKey || `chamii:${side}`);
  const gazeDurationS = (motionBoost ? 2.6 : 3.2) + seed * (motionBoost ? 1.4 : 2.1);
  const saccadeDurationS = (motionBoost ? 1.4 : 2.0) + seed * (motionBoost ? 0.9 : 1.4);
  const gazeDuration = `${gazeDurationS.toFixed(2)}s`;
  const gazeDelay = `${(-seed * gazeDurationS).toFixed(2)}s`;
  const saccadeDuration = `${saccadeDurationS.toFixed(2)}s`;
  const saccadeDelay = `${(-seed * saccadeDurationS).toFixed(2)}s`;
  const irisSize = `${Math.round(clamp(profile.irisScale, 0.3, 0.9) * 86)}%`;
  const pupil = Math.round(clamp(profile.pupilScale, 0.08, 0.9) * 54);
  const irisBg = profile.irisBg;
  const sparkle = profile.sparkleColor;
  const irisGlow = profile.glow && profile.glowColor ? `0 0 18px ${profile.glowColor}, 0 0 48px rgba(64,224,208,0.15)` : undefined;
  const gazeAnimation = `chamiiGaze ${gazeDuration} ease-in-out infinite ${gazeDelay}`;
  const saccadeAnimation = `chamiiSaccade ${saccadeDuration} cubic-bezier(0.2,0.9,0.2,1) infinite ${saccadeDelay}`;
  const jellyAnimation = profile.jelly ? 'chamiiJelly 1.6s ease-in-out infinite' : undefined;
  const playState = active ? 'running' : 'paused';
  const blinkKind = blinkToken?.kind || 'single';
  const blinkId = Number(blinkToken?.id || 0);
  const shouldBlink = active && blinkId > 0 && (blinkKind === 'single' || blinkKind === 'double' || blinkKind === 'slow' || blinkKind === 'winkLeft' && isLeft || blinkKind === 'winkRight' && !isLeft);
  const blinkMs = (() => {
    if (blinkKind === 'slow') return motionBoost ? 460 : 560;
    if (blinkKind === 'double') return motionBoost ? 320 : 380;
    return motionBoost ? 180 : 220;
  })();
  const blinkAnim = (() => {
    if (blinkKind === 'double') return `chamiiBlinkDouble ${blinkMs}ms ease-in-out 1`;
    if (blinkKind === 'slow') return `chamiiBlinkSlow ${blinkMs}ms ease-in-out 1`;
    return `chamiiBlinkOnce ${blinkMs}ms ease-in-out 1`;
  })();
  return <div className="relative w-[32%] h-[60%]">
      {profile.lashes ? <div className="absolute -top-[18%] left-1/2 -translate-x-1/2 w-[72%] h-[18%] pointer-events-none">
          <div className="absolute left-[10%] top-[70%] w-[18%] h-[22%] bg-white/70 rounded-full rotate-[-16deg]" />
          <div className="absolute left-1/2 top-[56%] w-[18%] h-[22%] -translate-x-1/2 bg-white/70 rounded-full" />
          <div className="absolute right-[10%] top-[70%] w-[18%] h-[22%] bg-white/70 rounded-full rotate-[16deg]" />
        </div> : null}

      <div className="absolute inset-0 bg-white/95 overflow-hidden shadow-[0_18px_40px_rgba(0,0,0,0.35)]" style={{
      borderRadius: '999px',
      outline: profile.sticker ? `3px solid ${outlineColor}` : `2px solid ${outlineColor}`,
      outlineOffset: profile.sticker ? '1px' : '2px',
      transform: tilt ? `rotate(${tilt}deg)` : undefined
    }}>
        <div className="absolute left-1/2 top-1/2 pointer-events-none" style={{
        width: irisSize,
        height: irisSize,
        transform: 'translate(-50%,-50%)',
        borderRadius: '999px'
      }}>
          <div className="absolute inset-0" style={{
          animation: gazeAnimation,
          willChange: 'transform',
          animationPlayState: playState
        }}>
            <div className="absolute inset-0" style={{
            borderRadius: '999px',
            animation: saccadeAnimation,
            willChange: 'transform',
            animationPlayState: playState
          }}>
              <div className="absolute inset-0" style={{
              borderRadius: '999px',
              animation: jellyAnimation,
              willChange: jellyAnimation ? 'transform' : undefined,
              animationPlayState: playState
            }}>
                <div className="absolute inset-0 rounded-full" style={{
                background: irisBg,
                opacity: profile.jelly ? 0.92 : 1,
                boxShadow: irisGlow
              }} />
                {profile.ring ? <div className="absolute inset-[10%] rounded-full" style={{
                border: `3px solid ${mixHexColors('#ffffff', profile.accent, 0.35)}`,
                opacity: 0.5
              }} /> : null}
                {profile.boba ? <>
                    <div className="absolute right-[18%] bottom-[18%] w-[12%] aspect-square rounded-full bg-black/35" />
                    <div className="absolute right-[30%] bottom-[12%] w-[10%] aspect-square rounded-full bg-black/35" />
                    <div className="absolute right-[14%] bottom-[34%] w-[9%] aspect-square rounded-full bg-black/35" />
                  </> : null}
                {profile.sprinkles ? <>
                    <div className="absolute left-[18%] top-[62%] w-[8%] h-[16%] rounded-full bg-pink-300/65 rotate-[18deg]" />
                    <div className="absolute left-[28%] top-[70%] w-[7%] h-[14%] rounded-full bg-amber-300/70 rotate-[-14deg]" />
                    <div className="absolute left-[38%] top-[64%] w-[7%] h-[14%] rounded-full bg-emerald-300/65 rotate-[20deg]" />
                    <div className="absolute left-[52%] top-[70%] w-[7%] h-[14%] rounded-full bg-sky-300/70 rotate-[-16deg]" />
                    <div className="absolute left-[62%] top-[62%] w-[8%] h-[16%] rounded-full bg-violet-300/65 rotate-[12deg]" />
                  </> : null}
                {profile.dreamy ? <>
                    <div className="absolute left-[14%] top-[18%] w-[10%] aspect-square rounded-full bg-white/35 blur-[0.5px]" />
                    <div className="absolute left-[68%] top-[26%] w-[8%] aspect-square rounded-full bg-white/25 blur-[0.5px]" />
                    <div className="absolute left-[24%] top-[44%] w-[7%] aspect-square rounded-full bg-white/18 blur-[0.5px]" />
                  </> : null}
                {profile.pixel ? <>
                  </> : null}

                <ChamiiCutePupil kind={profile.pupilKind} color="#0b1220" sizePercent={pupil} />
                <div className="absolute left-[20%] top-[18%] w-[18%] aspect-square rounded-full bg-white/50" />
                {profile.sparkle ? <div className="absolute right-[10%] bottom-[16%] w-[18%] aspect-square rounded-full" style={{
                background: sparkle,
                opacity: 0.22,
                filter: 'blur(1px)'
              }} /> : null}
              </div>
            </div>
          </div>
        </div>

        <div className="absolute left-0 right-0 top-0 bg-black" style={{
        height: `${winkClose * 50}%`
      }} />
        <div className="absolute left-0 right-0 bottom-0 bg-black" style={{
        height: `${winkClose * 50}%`
      }} />

        {shouldBlink ? <div key={`blink:${blinkId}:${blinkKind}:${side}`} className="absolute inset-0 bg-black origin-center pointer-events-none" style={{
        transform: 'scaleY(0)',
        animation: blinkAnim,
        willChange: 'transform',
        animationPlayState: playState
      }} /> : null}
      </div>
    </div>;
}
